import { NgModule } from '@angular/core';
import { RouterModule, Routes, UrlSerializer } from '@angular/router';
import { canMatchPwaB2cCheckout } from '@lentau/checkout/application/matchers/can-match-pwa-b2c-checkout.matcher';
import { authGuard, bdGuard } from '@lentau/auth/application';
import { SlashUrlSerializer } from '@lentau/core/route/shared/url-serializers/slash-url-serializer';
import {
  DEFAULT_PRELOAD_DELAY_MS,
  DelayedPreloadStrategy,
  ORDERING_PRELOAD_DELAY_MS,
} from '@lentau/core/route/strategies/delayed-preload.strategy';
import { SearchRouteMatcher } from '@lentau/search/shared/search-route-matcher';
import { DomRouteUrls, RouteUrls } from '@lentau/ui-kit/core/enums/route.enum';
import { PROMO_DETAIL_ROUTE } from '@lentau/promo/promo-detail-lnt';

export const routes: Routes = [
  {
    path: RouteUrls.B2BGiftCardsOrder,
    loadComponent: () =>
      import('@lentau/gift-cards/application-cards/b2b/page/b2b-gift-cards-page.component').then(
        (component) => component.GiftCardsPageComponent
      ),
  },
  {
    path: RouteUrls.B2BGiftCardDetails,
    loadComponent: () =>
      import(
        '@lentau/gift-cards/application-cards/b2b/page/gift-card-details/b2b-gift-card-details-page.component'
      ).then((component) => component.B2BGiftCardDetailsPageComponent),
  },
  {
    path: RouteUrls.B2BPlasticGiftCardsOrder,
    loadComponent: () =>
      import(
        '@lentau/gift-cards/application-cards/b2b/page/plastic-gift-card/b2b-plastic-gift-cards-page.component'
      ).then((component) => component.PlasticGiftCardsPageComponent),
  },
  {
    path: RouteUrls.B2bGiftCards,
    loadComponent: () =>
      import('@lentau/landings/pro-card/pages').then((component) => component.ProLandingCardComponent),
  },
  {
    path: RouteUrls.B2BGiftCardBalance,
    loadComponent: () =>
      import('@lentau/gift-cards/application-cards/b2b/page/check-balance/b2b-check-balance-page.component').then(
        (component) => component.CheckBalancePageComponent
      ),
    canActivate: [authGuard],
  },
  {
    path: RouteUrls.Ordering,
    canMatch: [canMatchPwaB2cCheckout],
    loadComponent: () =>
      import('@lentau/checkout/pages/checkout-layout-manager/checkout-layout-manager.component').then(
        (m) => m.CheckoutLayoutManagerComponent
      ),
    data: { preload: true, preloadDelay: ORDERING_PRELOAD_DELAY_MS },
    children: [
      {
        path: '',
        loadChildren: () => import('@lentau/order/shell/ordering-page.routing').then((mod) => mod.ORDERING_ROUTES),
        data: { preload: true, preloadDelay: ORDERING_PRELOAD_DELAY_MS },
      },
    ],
  },
  {
    path: RouteUrls.Ordering,
    loadComponent: () =>
      import('@lentau/layout/template/features/components/layout-ordering/layout-ordering.component').then(
        (mod) => mod.LayoutOrderingComponent
      ),
    data: { preload: true, preloadDelay: ORDERING_PRELOAD_DELAY_MS },
    children: [
      {
        path: '',
        loadChildren: () => import('@lentau/order/shell/ordering-page.routing').then((mod) => mod.ORDERING_ROUTES),
        data: { preload: true, preloadDelay: ORDERING_PRELOAD_DELAY_MS },
      },
    ],
  },
  {
    path: RouteUrls.GiftCardsLanding,
    loadComponent: () =>
      import('@lentau/gift-cards/landings/card-types/pages/card-types-dom/card-types-dom.component').then(
        (component) => component.CardTypesDomPageComponent
      ),
  },
  {
    path: RouteUrls.GiftCardsAbout,
    loadComponent: () =>
      import(
        '@lentau/gift-cards/landings/about-gift-cards/pages/about-gift-cards-dom/about-gift-cards-dom.component'
      ).then((component) => component.AboutGiftCardsDomPageComponent),
  },
  {
    path: '',
    loadComponent: () =>
      import('@lentau/layout/template/features/components/layout/layout.component').then((mod) => mod.LayoutComponent),
    children: [
      {
        path: '',
        loadComponent: () =>
          import('@lentau/main/pages/main-page/main-page.component').then((mod) => mod.MainPageComponent),
        data: { preload: true, preloadDelay: DEFAULT_PRELOAD_DELAY_MS },
      },
      {
        path: RouteUrls.Basket,
        loadChildren: () => import('@lentau/cart/page/cart-page.module').then((m) => m.CartPageModule),
        data: { preload: true, preloadDelay: DEFAULT_PRELOAD_DELAY_MS },
      },
      {
        path: RouteUrls.Info,
        loadChildren: () => import('@lentau/info/pages/info.routing').then((m) => m.INFO_ROUTES),
      },
      {
        path: RouteUrls.Authentication,
        loadComponent: () => import('@lentau/auth/page'),
      },
      {
        path: RouteUrls.AuthSocialCallback,
        loadComponent: () =>
          import('@lentau/auth/features/components/social-auth-callback/social-auth-callback.component').then(
            (m) => m.SocialAuthCallbackComponent
          ),
      },
      {
        path: RouteUrls.Login,
        loadChildren: () =>
          import('@lentau/registration/b2b/page/b2b-registration.routing').then((m) => m.B2B_REGISTRATION_ROUTES),
      },
      {
        path: RouteUrls.Catalog,
        loadChildren: () => import('@lentau/catalog/page/catalog-page.routes').then((m) => m.CATALOG_PAGE_ROUTES),
        data: { preload: true, preloadDelay: DEFAULT_PRELOAD_DELAY_MS },
      },
      {
        path: RouteUrls.B2CGiftCardsOrder,
        loadComponent: () =>
          import('@lentau/gift-cards/application-cards/b2c/page/b2c-gift-cards-page.component').then(
            (component) => component.B2CGiftCardsPageComponent
          ),
      },
      {
        matcher: SearchRouteMatcher,
        loadChildren: () =>
          import('@lentau/search-redesign/listing/page/lib/search-page/search-page.routing.module').then(
            (m) => m.SearchPageRoutingModule
          ),
      },
      {
        path: `${RouteUrls.Product}/:slugName`,
        loadComponent: () =>
          import('@lentau/product-redesign/page/product-page/product-page.component').then(
            (m) => m.ProductPageComponent
          ),
        data: { preload: true, preloadDelay: DEFAULT_PRELOAD_DELAY_MS },
      },
      {
        path: `${DomRouteUrls.Products}/:slugName`,
        loadComponent: () =>
          import('@lentau/product-redesign/page/product-page/product-page.component').then(
            (m) => m.ProductPageComponent
          ),
        data: { preload: true, preloadDelay: DEFAULT_PRELOAD_DELAY_MS },
      },
      {
        path: `${RouteUrls.Brand}/:slug`,
        loadChildren: () => import('@lentau/brands/feature').then((m) => m.BRANDS_ROUTES),
      },
      {
        path: RouteUrls.MyAccount,
        loadChildren: () => import('@lentau/profile/shell/profile-page-routing.module'),
      },
      {
        path: RouteUrls.Action,
        loadComponent: () => import('@lentau/promo/promo-list-lnt/features').then((m) => m.ActionListComponent),
      },
      PROMO_DETAIL_ROUTE,
      {
        path: RouteUrls.Landing,
        loadChildren: () => import('@lentau/landings/pages/landing.routing').then((m) => m.LANDING_ROUTES),
      },
      {
        path: RouteUrls.DynamicLandings,
        loadChildren: () => import('@lentau/landings/pages/dynamic-landing.routing').then((m) => m.LANDINGS_ROUTES),
      },
      {
        path: RouteUrls.CompaniesSwitcher,
        loadComponent: () =>
          import('@lentau/profile/organizations/companies-switcher/page/companies-switcher-page.component').then(
            (m) => m.CompaniesSwitcherPageComponent
          ),
      },
      {
        path: RouteUrls.BdCoupon,
        canActivate: [bdGuard],
        loadChildren: () => import('@lentau/profile/shell/profile-page-routing.module'),
      },
      {
        path: 'similar-products',
        data: { header: 'Похожие товары' },
        loadChildren: () =>
          import('@lentau/listing/pages/similar-products/similar-products.page').then(
            (m) => m.SimilarProductsPageModule
          ),
      },
      {
        path: RouteUrls.NotFound,
        loadComponent: () =>
          import('@lentau/not-found/page/not-found-page/not-found-page.component').then((m) => m.NotFoundPageComponent),
        data: { status: 404 },
      },
      {
        path: '**',
        loadComponent: () =>
          import('@lentau/not-found/page/not-found-page/not-found-page.component').then((m) => m.NotFoundPageComponent),
        data: { status: 404 },
      },
    ],
  },
];

@NgModule({
  imports: [
    RouterModule.forRoot(routes, {
      initialNavigation: 'enabledNonBlocking',
      anchorScrolling: 'enabled',
      paramsInheritanceStrategy: 'emptyOnly',
      bindToComponentInputs: true,
      onSameUrlNavigation: 'reload',
      preloadingStrategy: DelayedPreloadStrategy,
    }),
  ],
  exports: [RouterModule],
  providers: [
    {
      provide: UrlSerializer,
      useClass: SlashUrlSerializer,
    },
  ],
})
export class AppRoutingModule {}
